
import { useCallback, useMemo } from "react";
import { batch, useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router";
import classNames from "classnames";

import { FeatureFlagsEnum, LanguagesAbbr, LanguagesAbbrV2 } from '../../../../types/working-model';
import { useTypedSelector } from "../../../../store/reducers/use-typed-selector";
import Icon, { ICONS, COLORS } from '../../../general-ui/icon';
import { blurEditor, loadWorkingEvent } from '../../../../store/actions/admin/create-event';
import { ParamsProps } from '../../../live-event/live-event';
import { showAlert } from "@general-ui/alert/alert-service";
import { ALERT_TYPES } from "@general-ui/alert/alert";
import Select from "@general-ui/select/select";
import languageMap from '../../../../utils/translate-langs-map.json';
import languageMapV2 from '../../../../utils/translate-langs-map-v2.json';

import './editor-language-selector.scss';

export function switchingLanguageAlert(): void {
	showAlert({
		message: "Fetching translations",
		description: "Please wait a moment while we get new translations",
		duration: 4000,
		type: ALERT_TYPES.POSITIVE
	});
}

interface ILanguageSelector {
	className?: string;
	// If you want to use the language selector but not have it set the entire event's languages.
	// Must supply all 3 to correctly control this component from another component.
	handleSelectOverride?: (language: LanguagesAbbr) => void;
	languagesOverride?: LanguagesAbbr[];
	selectedLanguageOverride?: LanguagesAbbr;
}

const EditorLanguageSelector = ({ className, handleSelectOverride, languagesOverride, selectedLanguageOverride }: ILanguageSelector) => {
	const token = useTypedSelector(state => state.AuthReducer.token);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const eventLanguages = useTypedSelector(state => state.CreateEventReducer.workingEvent?.settings.i18n?.event_languages);
	const featureFlags = useTypedSelector(state => state.FeatureFlagsReducer.featureFlags);

	const dispatch = useDispatch();
	const history = useHistory();
	const { id, language: paramLanguage } = useParams() as ParamsProps;

	const languages: LanguagesAbbr[] = useMemo(() =>
		eventLanguages
			? eventLanguages
			: workingEvent?.sessions?.reduce((acc: string[], cur) => [...acc, ...cur.languages], []) as LanguagesAbbr[] || [],
		[workingEvent, eventLanguages]
	);

	const redirect = useCallback((language: LanguagesAbbr) => {
		// must construct a pathname that includes the current language and it MUST begin with a slash and either end with a slash or be the end of the string
		// this is to prevent a path with a component like '/pages/french-page/fr' navigating to 'en' to change to '/pages/enench-page/fr'

		// the escape chars are not useless, they are needed to escape the forward slashes, eslint is wrong
		// eslint-disable-next-line no-useless-escape
		const regex = new RegExp(`\/(${paramLanguage})($|\/)`);
		history.replace(history.location.pathname.replace(regex, `/${language}/`).replace(/\/$/, ''));
	}, [history, paramLanguage]);

	const handleLanguageClick = async (language: LanguagesAbbr) => {
		if (!token) return;

		switchingLanguageAlert();
		batch(() => {
			dispatch(blurEditor(true));
			dispatch(loadWorkingEvent(id, token));
		});

		redirect(language);
	};

	const mapLanguagesToSelectState = (languages: LanguagesAbbr[]) =>
		languages
			.sort((a, b) => (languageMap[a]) > (languageMap[b]) ? 1 : -1)
			.map(language => ({
				label: languageMap[language],
				value: language,
				icon: <img className="circle-flag" src={`https://assets.brandlive.com/assets/flags/round/${language}.svg`} />,
				jsx: <>{languageMap[language]} <span className="language-code">{language}</span></>
			}));

	const getCustomSelectDisplay = () => {
		const languageToUse = selectedLanguageOverride ?? paramLanguage;

		return (
			<>
				<img className="circle-flag" src={`https://assets.brandlive.com/assets/flags/round/${languageToUse}.svg`} /> {languageMap[languageToUse]} <span className="editor-language-selector-language-code">{languageToUse}</span>
			</>
		);
	};

	const languageSelectOptions = languagesOverride
		? mapLanguagesToSelectState(languagesOverride.filter(language =>
			selectedLanguageOverride
				? language !== selectedLanguageOverride
				// Handles the case of overriding which languages are selectable, but
				// state control is still within this component. Ex: session page selector
				// is still URL param based but you don't want all of the event languages
				: language !== paramLanguage
		))
		: mapLanguagesToSelectState(languages.filter(language => language !== paramLanguage));

	if (!featureFlags[FeatureFlagsEnum.ui_ai_captions] || !languageSelectOptions.length) return null;

	return (
		<div className={classNames("editor-language-selector", className)}>
			<Select
				containerClass="editor-language-selection"
				options={languageSelectOptions}
				showOptionIcon
				small
				noMaxHeight
				selected={selectedLanguageOverride ?? paramLanguage}
				onChange={handleSelectOverride ?? handleLanguageClick}
				ignorePositioning
				optionContainerTop={70}
				hideEmptyOption
				makeControlledComponent
				mainIcon={<Icon name={ICONS.KEYBOARD_ARROW_DOWN} size={14} color={COLORS.WHITE} />}
				iconIfOpen={<Icon name={ICONS.KEYBOARD_ARROW_UP} size={14} color={COLORS.WHITE} />}
				customSelectedOptionDisplay={getCustomSelectDisplay()}
				customOptionDisplay
			/>
		</div>
	);
};

export default EditorLanguageSelector;
